import { Footer } from "@/components/Footer";
import { PageMeta } from "@/components/site/blocks";
import { Band, Closing, Faqs, Hero, In, List, P, Related, Src, Steps, Table, WhatIs, WorkflowCard } from "@/components/seo/kit";

// /integrations/email-to-hubspot. Plan: gtm/products/clearmakes-inbox/seo/pages/email-to-hubspot.md
// Facts opened on 2026-09-29; sources are linked on the page.

const PATH = "/integrations/email-to-hubspot";

const faqs = [
  {
    q: "Will it create a new HubSpot contact for every email?",
    a: "Only for the emails you describe. Say “only new enquiries” and “only if they are not in HubSpot yet”, then check the steps before you switch it on. Newsletters, receipts and replies from existing customers can be told apart with a check step.",
  },
  {
    q: "Which details can it put into HubSpot?",
    a: "Whatever you name that the email contains: usually name, email address, company, phone number and what they asked for. The workflow pulls these out with AI, so check the first few contacts it creates.",
  },
  {
    q: "Does it work with Outlook as well as Gmail?",
    a: "Yes. Connect Outlook instead of Gmail and describe the same workflow. You can also connect both and have one workflow for each mailbox.",
  },
  {
    q: "Can it also reply to the lead?",
    a: "Yes. Add “draft a reply for me to check” or “reply straight away” to the sentence. Drafting is the safer start; you can switch to sending once you trust the replies.",
  },
  {
    q: "How much does it cost?",
    a: "Free covers 3 connected apps, 2 new workflows a month and 100 runs a month: enough for Gmail, HubSpot and one more app. Starter is $29 a month for 5,000 runs. One run is one email that starts the workflow, with all its steps.",
  },
];

const EmailToHubspot = () => (
  <>
    <PageMeta
      title="Add Email Leads to HubSpot Automatically"
      description="Turn new enquiries in Gmail or Outlook into HubSpot contacts with name, company and request filled in, and draft a reply. Set up in one sentence."
      path={PATH}
      crumbs={[
        { name: "Home", path: "/" },
        { name: "Integrations", path: "/integrations" },
        { name: "Email to HubSpot", path: PATH },
      ]}
    />
    <main>
      <Hero
        eyebrow="Integrations · Gmail, Outlook and HubSpot"
        title="Add email leads to HubSpot automatically"
        lead="When a new enquiry lands in Gmail or Outlook, create the HubSpot contact with the details filled in, and draft a reply while the lead is still warm."
        secondary={{ label: "See every integration", href: "/integrations" }}
      />

      <WhatIs title="What is the email to HubSpot workflow in ClearMakes Inbox?">
        It is a ClearMakes Inbox workflow that watches Gmail or Outlook for new enquiries, pulls out the sender's name, company and request,
        and creates or updates the contact in HubSpot. It can also draft a reply for you to check. You describe it in one sentence. There is a
        free plan; paid plans start at $29 a month.
      </WhatIs>

      <Band
        tone="pale"
        title="What the email to HubSpot workflow does"
        answer="It saves you copying details from an email into HubSpot by hand. You decide which emails count as leads and which fields to fill."
      >
        <div className="grid gap-5 lg:grid-cols-2">
          <WorkflowCard
            sentence="When a new enquiry arrives in Gmail, add the person to HubSpot with their name, company and what they asked for, and draft a reply for me to check."
            steps={[
              "When an email arrives in Gmail",
              "Stop if it is not a new enquiry",
              "Pull out the details: name, company, request",
              "Create the contact in HubSpot",
              "Draft a reply",
            ]}
          />
          <WorkflowCard
            sentence="When someone emails sales@ in Outlook asking for a quote, add them to HubSpot and tell #sales in Slack."
            steps={[
              "When an email arrives in Outlook",
              "Check it asks for a quote",
              "Pull out the details: name, company, what they need",
              "Create the contact in HubSpot",
              "Post to #sales in Slack",
            ]}
          />
        </div>
        <P>
          As one person asking for faster email triggers put it, “lead response time is critical”. Putting the contact in HubSpot and a draft in your
          mailbox in the same run means nobody has to do both by hand. (Quote from a{" "}
          <Src href="https://community.zapier.com/show-tell-5/instant-gmail-or-outlook-email-triggers-19667">Zapier community thread</Src>, read 29 September 2026.)
        </P>
      </Band>

      <Band
        tone="white"
        title="How to set up email to HubSpot"
        answer="Connect your mailbox and HubSpot, describe the workflow in a sentence, and check each step before you switch it on. You need a HubSpot account where you are allowed to connect apps."
      >
        <Steps
          items={[
            { title: "Create a free ClearMakes account", body: <>Start from <In href="/products/inbox">ClearMakes Inbox</In>.</> },
            { title: "Connect Gmail or Outlook", body: "Open Apps and connect the mailbox the enquiries arrive in." },
            { title: "Connect HubSpot", body: "Find HubSpot in Apps, choose Connect and sign in. HubSpot shows what the app may do before you agree." },
            { title: "Describe the workflow", body: "Name the fields you want filled, and say “only if they are not in HubSpot yet” if you want no duplicates." },
            { title: "Check the HubSpot step", body: "Inbox picks the HubSpot action from HubSpot's own list, so it can only choose one that exists. Check it is the one you meant." },
            { title: "Switch it on and check the first contacts", body: "Each run lists what it pulled out and what it created, so you can compare it with the email." },
          ]}
        />
      </Band>

      <Band
        tone="pale"
        title="Other ways to get email leads into HubSpot"
        answer="HubSpot's own tools may already log emails for you, depending on your HubSpot plan, so check those first. Automation tools are for choosing which emails become contacts and filling the fields from the email itself."
      >
        <Table
          caption="Ways to add email leads to HubSpot"
          head={["Way", "What it costs", "Good at", "Watch out for"]}
          rows={[
            ["By hand", "Your time", "Full control", "Slow replies; details copied wrong; leads forgotten on busy days"],
            ["HubSpot's own inbox and email logging", "Depends on your HubSpot plan", "Staying inside HubSpot", "HubSpot users report a new contact for every email when it is set up broadly; check your settings"],
            [
              "Zapier (Gmail “New Email” → HubSpot “Create or Update Contact”)",
              "Free: 100 tasks a month, two-step Zaps. Professional: $29.99 a month for 750 tasks",
              "Exact field mapping; many HubSpot actions",
              "Each successful action is a task; pulling details out of free text needs extra steps",
            ],
            [
              "ClearMakes Inbox",
              "Free: 3 apps, 100 runs a month. Starter: $29 a month for 5,000 runs",
              "Reading the email in plain words; contact, reply and Slack post in one run",
              "AI can misread an email; check the first contacts",
            ],
          ]}
          note={
            <>
              Checked on 29 September 2026: <Src href="https://zapier.com/apps/gmail/integrations/hubspot">Zapier Gmail + HubSpot</Src>, <Src href="https://zapier.com/pricing">Zapier pricing</Src>.
              The HubSpot report comes from a HubSpot Community thread titled “Stop creating a new contact for every email I get”.
            </>
          }
        />
      </Band>

      <Band
        tone="white"
        title="Limits, and who this is not for"
        answer="If your leads come in through a web form, use HubSpot's own forms: they write straight into HubSpot. This workflow is for leads that arrive as ordinary emails."
        narrow
      >
        <List
          items={[
            "Not a CRM. HubSpot stays your record of the customer; Inbox only fills it in.",
            "AI pulls details out of free text. It can miss a phone number or read a signature wrong, so look at the first contacts.",
            "Free plan: 3 apps and 100 runs a month. A busy sales mailbox needs Starter or Growth.",
            <>Replying fast matters as much as logging: see <In href="/lead-response-automation">lead response automation for email</In>.</>,
          ]}
        />
      </Band>

      <Faqs items={faqs} />

      <Related
        links={[
          { href: "/lead-response-automation", label: "Lead response automation", desc: "Reply to new email enquiries in minutes." },
          { href: "/gmail-integration", label: "Gmail automation", desc: "What a workflow can do with a new Gmail email." },
          { href: "/outlook-integration", label: "Outlook email automation", desc: "The same for Outlook mailboxes." },
          { href: "/integrations/gmail-to-slack", label: "Gmail to Slack", desc: "Tell the team in Slack when a lead arrives." },
          { href: "/for-founders", label: "For founders", desc: "Hand routine email work to workflows." },
          { href: "/pricing", label: "ClearMakes Inbox pricing", desc: "Free, Starter, Growth and Enterprise." },
        ]}
      />

      <Closing title="Put new email leads into HubSpot for you." line="Connect your mailbox and HubSpot, type one sentence, and check the steps." />
    </main>
    <Footer />
  </>
);

export default EmailToHubspot;
